import React, { useState, useEffect, useRef } from 'react';
import Input, { InputProps } from './Input';
import { Button } from './Button';
import Pill, { PillProps } from './Pill';
import Icon from './Icon';
import { cn } from '@/app/lib/utils';

export interface TagsInputProps {
  initialTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  pillProps?: PillProps;
  inputProps?: InputProps;
  isNumeric?: boolean;
  maxLength?: number;
  maxTags?: number;
  showAddButton?: boolean;
  wrapperClassName?: string;
  type?: 'text' | 'number';
  tagsPlacement?: 'inside' | 'outside';
  validateTag?: (tag: string) => boolean;
  onTagValidationError?: (tag: string) => void;
}

const TagsInput: React.FC<TagsInputProps> = ({
  initialTags = [],
  onTagsChange,
  pillProps,
  inputProps,
  isNumeric,
  maxLength,
  maxTags,
  showAddButton = false,
  wrapperClassName,
  type = 'text',
  tagsPlacement = 'inside',
  validateTag,
  onTagValidationError,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState<string[]>(initialTags);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMaxTagsReached = !!maxTags && tags.length >= maxTags;

  useEffect(() => {
    setTags(initialTags || []);
  }, [initialTags]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (isMaxTagsReached) {
      return;
    }
    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    if (isNumeric) {
      value = value.replace(/\D/g, '');
    }

    setInputValue(value);
  };

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue !== '' && (!maxTags || tags.length < maxTags)) {
      if (validateTag && !validateTag(trimmedValue)) {
        onTagValidationError?.(trimmedValue);
        return;
      }
      if (!tags.includes(trimmedValue)) {
        const newTags = [...tags, trimmedValue];
        setTags(newTags);
        setInputValue('');
        if (onTagsChange) {
          onTagsChange(newTags);
        }
      }
    }
  };

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number' && e.key === 'e') {
      e.preventDefault();
    }
    if (isMaxTagsReached) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
    if (onTagsChange) {
      onTagsChange(newTags);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (inputValue.trim() !== '') {
      handleAddTag();
    }
    inputProps?.onBlur?.(e);
  };

  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (isMaxTagsReached) {
      return;
    }
    const clipboardData = e.clipboardData;
    const pastedData = clipboardData.getData('text');
    const pastedtags = pastedData.split(',');
    const filteredTags = pastedtags.filter(tag => tag.trim() !== '').map(tag => tag.trim());
    const validTags = validateTag ? filteredTags.filter(tag => validateTag(tag)) : filteredTags;
    const invalidTag = validateTag ? filteredTags.find(tag => !validateTag(tag)) : undefined;
    if (invalidTag) {
      onTagValidationError?.(invalidTag);
    }
    const newTags = Array.from(new Set([...tags, ...validTags])).slice(0, maxTags);
    setTags(newTags);
    setInputValue('');
    if (onTagsChange) {
      onTagsChange(newTags);
    }
  };

  const renderTags = () =>
    tags.map((tag, index) => (
      <Pill
        className="min-w-auto w-auto max-w-full flex items-center overflow-hidden"
        key={index}
        rounded={false}
        color="neutral"
        {...pillProps}
      >
        <span className="overflow-hidden truncate">{tag}</span>
        <span onClick={() => handleRemoveTag(tag)} className="ml-1 cursor-pointer inline-flex">
          <Icon name="cross-fill" size={4} className="text-muted-foreground hover:text-sub-900" />
        </span>
      </Pill>
    ));

  return (
    <div className={cn(tagsPlacement === 'outside' && 'space-y-2')}>
      <div
        className={cn(
          'flex min-h-[38px] max-h-[130px] cursor-text flex-wrap items-stretch gap-1.5 overflow-y-auto rounded-md border border-input bg-white dark:bg-input/30 py-1.5 ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          tagsPlacement === 'inside' && tags.length > 0 && 'px-2',
          wrapperClassName
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {tagsPlacement === 'inside' && renderTags()}

        <Input
          type={type === 'number' ? 'number' : 'text'}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyPress}
          {...inputProps}
          ref={inputRef}
          disabled={inputProps?.disabled || isMaxTagsReached}
          placeholder={tags.length > 0 ? '' : inputProps?.placeholder}
          wrapperClassName={cn(
            'h-6 min-h-0 flex-1 min-w-[100px] border-none bg-transparent shadow-none dark:bg-transparent focus:border-none focus:ring-0 focus-within:border-0 focus-within:outline-0 focus-within:ring-0 focus-within:ring-offset-0',
            inputProps?.wrapperClassName
          )}
          className={cn('h-full min-h-0 w-full p-0 px-2', inputProps?.className)}
          onBlur={handleInputBlur}
          onPaste={handleInputPaste}
        />
        {showAddButton && (
          <Button
            onClick={handleAddTag}
            className="rounded-l-none border-l-0"
            variant="outline"
            disabled={isMaxTagsReached}
          >
            Add
          </Button>
        )}
      </div>
      {tagsPlacement === 'outside' && tags.length > 0 && (
        <div className="flex flex-col items-start gap-1.5 overflow-hidden">{renderTags()}</div>
      )}
    </div>
  );
};

export default TagsInput;
